import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from '../ui/Card';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import {
  getAllTemplates,
  setDefaultTemplate,
  createTemplate,
  updateTemplate,
  getTemplateById
} from '../../service/template/TemplateServices';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { PermissionVariables } from '../../shared/constant/PermissionVariables';
import { CreateTemplateDto } from '../../types/temp.types';
import { ContentEditable } from './ContentEditable';

interface Template {
  template_id: number;
  template_name: string;
  template_type: string;
  template_content: string;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  // UI specific properties
  is_default?: boolean;
  description?: string;
  id?: string;
  name?: string;
}

export const TemplateList: React.FC = () => {
  const editableRef = useRef<HTMLDivElement>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [defaultTemplate, setDefaultTemplateId] = useState<string | null>(null);
  const [showSetDefaultDialog, setShowSetDefaultDialog] = useState<boolean>(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState<boolean>(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [templateFormData, setTemplateFormData] = useState<CreateTemplateDto>({
    template_name: '',
    template_type: 'standard',
    template_content: ''
  });
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );
  const localization = useSelector((state: any) => state.localization);

  const templateTypes = [
    { label: localization?.Standard || 'Standard', value: 'standard' },
    { label: localization?.Professional || 'Professional', value: 'professional' },
    { label: localization?.Modern || 'Modern', value: 'modern' },
    { label: localization?.Simple || 'Simple', value: 'simple' }
  ];

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);


  useEffect(() => {
    fetchTemplates();
  }, [dispatch]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const paginationParams = {
        Page: 1,
        Limit: 20,
        SortProperty: "template_name",
        SortBy: "ASC"
      };

      const response = await getAllTemplates(dispatch, paginationParams);

      if (response?.List) {
        // Transform API response to component's expected format
        const transformedTemplates = response.List.map((template: Template) => ({
          ...template,
          id: template.template_id.toString(),
          name: template.template_name,
          // In a real app, this would come from the API
          description: `${template.template_type} template`,
          // In a real app, we'd have a specific property for default templates
          isDefault: template.is_default
        }));

        setTemplates(transformedTemplates);

        // Find default template
        const defaultTemplate = transformedTemplates.find((t: any) => t.isDefault);
        if (defaultTemplate) {
          setDefaultTemplateId(defaultTemplate.id);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.current?.show({
        severity: 'error',
        summary: localization?.Error || 'Error',
        detail: localization?.FailedToLoadTemplates || 'Failed to load templates'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomizeTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const response = await getTemplateById(dispatch, templateId);

      if (response) {
        setCurrentTemplateId(parseInt(templateId));
        setTemplateFormData({
          template_name: response.template_name || '',
          template_type: response.template_type || 'standard',
          template_content: response.template_content || ''
        });
        setIsEditMode(true);
        setShowTemplateDialog(true);
        setActiveTabIndex(0); // Reset to edit tab
      }
    } catch (error) {
      console.error('Error fetching template details:', error);
      toast.current?.show({
        severity: 'error',
        summary: localization?.Error || 'Error',
        detail: localization?.FailedToLoadTemplateDetails || 'Failed to load template details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = (template: any) => {
    setPreviewTemplate(template);
    setShowPreviewDialog(true);
  };

  const closePreviewDialog = () => {
    setShowPreviewDialog(false);
    setPreviewTemplate(null);
  };

  const openSetDefaultDialog = () => {
    setSelectedTemplate(defaultTemplate);
    setShowSetDefaultDialog(true);
  };

  const handleSetDefaultTemplate = async () => {
    if (selectedTemplate) {
      try {
        const response = await setDefaultTemplate(dispatch, selectedTemplate);

        if (response) {
          // Update local state
          const updatedTemplates = templates.map(template => ({
            ...template,
            isDefault: template.template_id.toString() === selectedTemplate
          }));

          setTemplates(updatedTemplates);
          setDefaultTemplateId(selectedTemplate);
          toast.current?.show({
            severity: 'success',
            summary: localization?.Success || 'Success',
            detail: localization?.DefaultTemplateUpdatedSuccessfully || 'Default template updated successfully'
          });
        }
      } catch (error) {
        console.error('Error setting default template:', error);
        toast.current?.show({
          severity: 'error',
          summary: localization?.Error || 'Error',
          detail: localization?.FailedToUpdateDefaultTemplate || 'Failed to update default template'
        });
      } finally {
        setShowSetDefaultDialog(false);
      }
    }
  };

  const openCreateTemplateDialog = () => {
    setIsEditMode(false);
    setCurrentTemplateId(null);
    setTemplateFormData({
      template_name: '',
      template_type: 'standard',
      template_content: ''
    });
    setShowTemplateDialog(true);
    setActiveTabIndex(0); // Start with edit tab
  };

  const handleFormChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.value || e.value;

    setTemplateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTemplateFormData(prev => ({
      ...prev,
      template_content: e.target.value
    }));
  };

  const saveTemplate = async () => {
    try {
      setLoading(true);
      let response;

      if (isEditMode && currentTemplateId) {
        // Update existing template
        response = await updateTemplate(dispatch, currentTemplateId, templateFormData);
        if (response) {
          toast.current?.show({
            severity: 'success',
            summary: localization?.Success || 'Success',
            detail: localization?.TemplateUpdatedSuccessfully || 'Template updated successfully'
          });
        }
      } else {
        // Create new template
        response = await createTemplate(dispatch, templateFormData);
        if (response) {
          toast.current?.show({
            severity: 'success',
            summary: localization?.Success || 'Success',
            detail: localization?.TemplateCreatedSuccessfully || 'Template created successfully'
          });
        }
      }

      // Refresh templates list
      await fetchTemplates();
      setShowTemplateDialog(false);

    } catch (error) {
      console.error('Error saving template:', error);
      toast.current?.show({
        severity: 'error',
        summary: localization?.Error || 'Error',
        detail: isEditMode
          ? (localization?.FailedToUpdateTemplate || 'Failed to update template')
          : (localization?.FailedToCreateTemplate || 'Failed to create template')
      });
    } finally {
      setLoading(false);
    }
  };

  const defaultTemplateFooter = (
    <div className="flex justify-end gap-2">
      <Button
        label={localization?.Cancel || "Cancel"}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={() => setShowSetDefaultDialog(false)}
      />
      <Button
        label={localization?.SetAsDefault || "Set as Default"}
        icon="pi pi-check"
        className="p-button-primary"
        onClick={handleSetDefaultTemplate}
        disabled={!selectedTemplate}
      />
    </div>
  );

  const templateDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button
        label={localization?.Cancel || "Cancel"}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={() => setShowTemplateDialog(false)}
      />
      <Button
        label={isEditMode ? (localization?.Update || "Update") : (localization?.Create || "Create")}
        icon={isEditMode ? "pi pi-save" : "pi pi-plus-circle"}
        className="p-button-primary"
        onClick={saveTemplate}
        disabled={!templateFormData.template_name || !templateFormData.template_content}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center p-6">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
        <span className="ml-3">{localization?.LoadingTemplates || "Loading templates..."}</span>
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} />
      <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
        <h1 className="text-xl font-bold text-gray-800">{localization?.InvoiceTemplates || "Invoice Templates"}</h1>

        <div className="flex gap-2">
          <Button
            label={localization?.SetDefault || "Set Default"}
            icon="pi pi-check-circle"
            className="p-button-outlined p-button-sm"
            onClick={openSetDefaultDialog}
          />
          {/* {userPermissions?.includes(PermissionVariables.createTemplate) && (
            <Button
              label={localization?.CreateTemplate || "Create Template"}
              icon="pi pi-plus"
              className="p-button-sm"
              onClick={openCreateTemplateDialog}
            />
          )} */}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map((template: any) => (
          <Card key={template.id} className="relative shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 card_content_padding">
            <div className="p-0">
              <div className="relative mb-3">
                {/* Template preview */}
                <div className="w-full h-48 overflow-hidden rounded-md border border-gray-300 border-solid">
                  <iframe
                    srcDoc={template.template_content}
                    title={template.name}
                    className="w-full h-full border-0 transform scale-50 origin-top-left"
                    sandbox="allow-same-origin"
                    style={{ width: '200%', height: '200%' }}
                  />
                </div>
                {template.isDefault && (
                  <span className="absolute top-2 right-2 bg-primary text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm">
                    {localization?.Default || "Default"}
                  </span>
                )}
              </div>

              <div className="p-0">
                <h2 className="text-lg font-semibold text-gray-800 mb-1">{template.name}</h2>
                <p className="text-gray-600 text-sm mb-3">{template.description}</p>

                <div className="flex justify-end gap-2">
                  <Button
                    label={localization?.Preview || "Preview"}
                    icon="pi pi-eye"
                    className="p-button-outlined p-button-sm"
                    onClick={() => handlePreviewTemplate(template)}
                  />
                  <Button
                    label={localization?.Customize || "Customize"}
                    icon="pi pi-pencil"
                    className="p-button-sm"
                    onClick={() => handleCustomizeTemplate(template.id)}
                  />
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Set Default Template Dialog */}
      <Dialog
        header={localization?.SetDefaultTemplate || "Set Default Template"}
        visible={showSetDefaultDialog}
        style={{ width: '90%', maxWidth: '550px' }}
        footer={defaultTemplateFooter}
        onHide={() => setShowSetDefaultDialog(false)}
      >
        <div className="p-0">
          <p className="mb-4">
            {localization?.SelectDefaultTemplateText || "Select the invoice template you want to use as the default for new invoices:"}
          </p>

          <div className="grid md:grid-cols-3 gap-3">
            {templates.map((template: any) => (
              <div key={template.id} className="flex align-items-center">
                <RadioButton
                  inputId={template.id}
                  name="templateOption"
                  value={template.id}
                  onChange={(e) => setSelectedTemplate(e.value)}
                  checked={selectedTemplate === template.id}
                />
                <label htmlFor={template.id} className="ml-2 font-medium">
                  {template.name}
                </label>
              </div>
            ))}
          </div>
        </div>
      </Dialog>

      {/* Preview Template Dialog */}
      <Dialog
        header={
          <div className="flex items-center">
            <i className="pi pi-eye mr-2 text-primary"></i>
            <span>{localization?.TemplatePreview || "Template Preview"}: {previewTemplate?.template_name || previewTemplate?.name}</span>
          </div>
        }
        visible={showPreviewDialog}
        style={{ width: '90%', maxWidth: '900px' }}
        onHide={closePreviewDialog}
        maximizable
        footer={
          <div className="flex justify-end">
            <Button
              label={localization?.Close || "Close"}
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={closePreviewDialog}
            />
          </div>
        }
      >
        <div className="bg-gray-100 rounded-md shadow-inner p-4 mt-4" style={{ minHeight: '600px' }}>
          <div className="bg-white rounded-md shadow-md mx-auto p-4" style={{ maxWidth: '800px' }}>
            <iframe
              srcDoc={previewTemplate?.template_content}
              title={`Preview: ${previewTemplate?.template_name || previewTemplate?.name}`}
              className="w-full border-0 rounded-md"
              style={{ height: '570px' }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </Dialog>

      {/* Create/Edit Template Dialog */}
      <Dialog
        header={
          <div className="flex items-center">
            <i className={`pi ${isEditMode ? 'pi-pencil' : 'pi-plus-circle'} mr-2 text-primary`}></i>
            <span>{isEditMode ? (localization?.EditTemplate || "Edit Template") : (localization?.CreateTemplate || "Create Template")}</span>
          </div>
        }
        visible={showTemplateDialog}
        style={{ width: '75%', maxWidth: '900px' }}
        footer={templateDialogFooter}
        onHide={() => setShowTemplateDialog(false)}
        maximizable
        className="template-dialog"
      >

        <div className="bg-gray-100 rounded-md shadow-inner p-4 mt-4" style={{ height: '500px', overflow: 'auto' }}>
          <div className="bg-white rounded-md shadow-sm mx-auto" style={{ maxWidth: '800px' }}>
            <ContentEditable
              initialContent={templateFormData.template_content}
              onContentChange={(content) => {
                setTemplateFormData(prev => ({
                  ...prev,
                  template_content: content
                }));
              }}
              className="min-h-full outline-none"
              style={{ height: '480px' }}
            />

          </div>
        </div>

      </Dialog>
    </div>
  );
};